import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { WatermarkElementTypes, type IWatermarkElement, type IWatermarkSet } from '@/types/watermarks.types';
import { Case, SwitchCase } from '@ui/switch-case/switch-case';
import styles from './watermark.module.css';
import { updateTransforms } from './utils/handle-mouse-move';
import { onMouseDown } from './utils/handle-mouse-down';
import type { TransformRef } from './watermark.types';
import { useWatermarkFonts } from './utils/use-watermark-fonts';
import { getContainerScale } from './utils/get-container-size';
import { withUpdatedWatermarkDimensions } from './utils/with-updated-watermark-dimensions';
import { withSortedElements } from './utils/with-sorted-elements';
import ControlBox from './elements/control-box';
import EmailEl from './elements/email';
import DateTimeEl from './elements/datetime';
import TiledEl from './elements/tiled-element';
import { isDefined } from '@/utils/is-defined';
import ImageEl from './elements/image';
import TextEl from './elements/text';
import { generateTiledImage } from './utils/generate-tiled-image';
import { generateTiledText } from './utils/generate-tiled-text';
import { getTextForElement } from './utils/get-text-for-element';

const DEBUG = false;

const initTransforms: TransformRef = {
  centerTransform: undefined,
  mouseDown: false,
  pageScale: 1,
  parentX: 0,
  parentY: 0,
  selectedDiv: undefined,
  selectedDivOffsetX: undefined,
  selectedDivOffsetY: undefined,
  selectedElement: undefined,
  startX: 0,
  startY: 0,
  transform: undefined,
  transformHandle: undefined,
  transformScale: undefined,
  transformType: '',
};

export default function WatermarkCss(props: {
  watermark: IWatermarkSet;
  height?: number;
  width?: number;
  onElementChange?: (el: IWatermarkElement) => void;
}) {
  const { onElementChange } = props;
  const mainRef = useRef<HTMLDivElement | null>(null);
  const elementRefs = useRef<Map<string, HTMLDivElement | null>>(new Map());
  const debugDot = useRef<HTMLDivElement | null>(null);
  const [scale, setScale] = useState(1);
  const [wmDraft, setWmDraft] = useState(withSortedElements(props.watermark));
  const elementTypes = useRef<Map<string, WatermarkElementTypes>>(new Map());
  const [renderKey, setRenderKey] = useState(0); // State to force re-render
  const [imageLoadCount, setImageLoadCount] = useState(0);
  const transforms = useRef<TransformRef>(initTransforms);
  const sortedElements = useMemo(() => withSortedElements(wmDraft).elements, [wmDraft]);
  const hasEverSet = useRef(false);
  const tileTimeout = useRef<NodeJS.Timeout | null>(null);
  const tiledImages = useRef<Map<IWatermarkElement['uuid'], string>>(new Map());
  const mounted = useRef(true);

  useWatermarkFonts(props.watermark);

  useEffect(() => {
    if (transforms.current.mouseDown) return;

    const rerender = async () => {
      try {
        for (const el of sortedElements) {
          if (el.type === WatermarkElementTypes.image && el.settings.repeat) {
            const url = await generateTiledImage({
              src: el.settings.image.url,
              height: 1080,
              width: 1920,
              padding: 40,
              imageHeight: (el.settings.dimensions.height ?? 100) * (el.settings.dimensions.scale ?? 1),
              imageWidth: (el.settings.dimensions.width ?? 100) * (el.settings.dimensions.scale ?? 1),
              rotation: 0,
            });

            if (url) {
              const prev = tiledImages.current.get(el.uuid);
              if (prev !== url) {
                tiledImages.current.set(el.uuid, url);
                setRenderKey((prev) => prev + 1);
                if (prev) {
                  try {
                    URL.revokeObjectURL(prev);
                  } catch {
                    // not there
                  }
                }
              }
            }

            continue;
          }

          const text = getTextForElement(el);
          if (el.settings.repeat && text) {
            const url = await generateTiledText({
              text: text,
              height: 1080,
              width: 1920,
              padding: 40,
              rotation: 0,
              scale: el.settings.dimensions.scale,
              textSettings: {
                ...el.settings.text,
                font_size: el.settings.text.font_size * el.settings.dimensions.scale,
              },
            });

            if (url) {
              const prev = tiledImages.current.get(el.uuid);
              if (prev !== url) {
                tiledImages.current.set(el.uuid, url);
                setRenderKey((prev) => prev + 1);
                if (prev) {
                  try {
                    URL.revokeObjectURL(prev);
                  } catch {
                    // not there
                  }
                }
              }
            }
          }
        }

        hasEverSet.current = true;
      } catch (e) {
        console.error(e);
      }
    };

    if (!hasEverSet) {
      rerender();
    }

    if (tileTimeout.current) {
      clearTimeout(tileTimeout.current);
    }

    tileTimeout.current = setTimeout(rerender, 300);
  }, [sortedElements]);

  // auto-center elements whose x/y are NaN (newly created or type just changed)
  useEffect(() => {
    if (!onElementChange) return;

    wmDraft.elements.forEach((el) => {
      if (isFinite(el.settings.dimensions.x) && isFinite(el.settings.dimensions.y)) return;
      const htmlEl = elementRefs.current.get(el.uuid);
      if (!htmlEl) return;

      // Image elements: wait until a real image has loaded before measuring
      if (el.type === WatermarkElementTypes.image) {
        const img = htmlEl.querySelector('img');
        if (!img || img.naturalWidth === 0) {
          // no image yet (empty src or not loaded) — attach a listener if a URL is pending
          if (img && !img.complete && el.settings.image.url) {
            img.addEventListener('load', () => setImageLoadCount((c) => c + 1), { once: true });
          }
          return;
        }
      }

      const elRect = htmlEl.getBoundingClientRect();
      if (elRect.width === 0 || elRect.height === 0) return;
      const centeredX = Math.round((1920 - elRect.width / scale) / 2);
      const centeredY = Math.round((1080 - elRect.height / scale) / 2);
      onElementChange({
        ...el,
        settings: {
          ...el.settings,
          dimensions: { ...el.settings.dimensions, x: centeredX, y: centeredY },
        },
      });
    });
  }, [wmDraft.elements, onElementChange, scale, imageLoadCount]);

  useEffect(() => {
    if (!transforms.current.mouseDown) {
      // clear cached tiled image for any element whose type changed
      for (const el of props.watermark.elements) {
        const prevType = elementTypes.current.get(el.uuid);
        if (prevType !== undefined && prevType !== el.type) {
          tiledImages.current.delete(el.uuid);
        }
        elementTypes.current.set(el.uuid, el.type);
      }
      setWmDraft(withSortedElements(props.watermark));
    }
  }, [props.watermark]);

  const handleMouseUp = useCallback(() => {
    const { selectedElement, transform } = transforms.current;
    if (selectedElement && transform) {
      const updater = withUpdatedWatermarkDimensions(selectedElement, elementRefs);
      const updatedElement = updater(selectedElement);

      if (updatedElement !== selectedElement) {
        setWmDraft((prev) => ({
          ...prev,
          elements: prev.elements.map((el) => (el.uuid === selectedElement.uuid ? updatedElement : el)),
        }));
        onElementChange?.(updatedElement);
      }

      // force a re-render to reset the control box placement
      setRenderKey((prev) => prev + 1);
    }

    setTimeout(() => {
      transforms.current.mouseDown = false;
      transforms.current.selectedElement = undefined;
      transforms.current.selectedDiv = undefined;
      transforms.current.transformHandle = undefined;
      transforms.current.transformType = '';
      transforms.current.centerTransform = undefined;
      transforms.current.transformScale = undefined;
    });
  }, [onElementChange]);

  useEffect(() => {
    const handleManualScale = () => {
      const el = mainRef.current;
      if (el) {
        const scale = getContainerScale(el, props.height, props.width);
        setScale(scale);
        transforms.current.pageScale = scale;
      }
    };

    setTimeout(handleManualScale);

    window.addEventListener('resize', handleManualScale);
    window.addEventListener('pointerup', handleMouseUp);
    return () => {
      window.removeEventListener('resize', handleManualScale);
      window.removeEventListener('pointerup', handleMouseUp);
    };
  }, [handleMouseUp, props.height, props.width]);

  useEffect(() => {
    const images = tiledImages.current;
    return () => {
      images.values().forEach((url) => URL.revokeObjectURL(url));
      mounted.current = false;
    };
  }, []);

  return (
    <div
      ref={mainRef}
      className={styles.watermarkContainer}
      style={
        {
          '--scale': scale,
          '--inverse-scale': Math.max(0, 1 / scale),
          ...(isDefined(props.height) ? { height: props.height } : {}),
          ...(isDefined(props.width) ? { width: props.width } : {}),
        } as CSSProperties
      }
      onPointerMove={updateTransforms(transforms, debugDot)}
      onPointerUp={handleMouseUp}
    >
      {DEBUG && <div ref={debugDot} style={{ height: 25, width: 25, background: 'red' }} />}
      {wmDraft.elements.map((el) => {
        if (el.type === WatermarkElementTypes.image && el.settings.repeat) {
          return <TiledEl key={el.uuid} el={el} tiledImages={tiledImages} />;
        }

        if (el.type !== WatermarkElementTypes.image && el.settings.repeat) {
          return <TiledEl key={el.uuid} el={el} tiledImages={tiledImages} />;
        }

        const { x, y, rotation, scale } = el.settings.dimensions;
        const safeX = isFinite(x) ? x : 0;
        const safeY = isFinite(y) ? y : 0;
        const safeRotation = isFinite(rotation) ? rotation : 0;
        const safeScale = isFinite(scale) && scale > 0 ? scale : 1;
        const t = new DOMMatrix([1, 0, 0, 1, safeX, safeY]);
        t.rotateSelf(safeRotation).scaleSelf(safeScale);

        const unpositioned = !isFinite(x) || !isFinite(y);

        return (
          <div
            key={el.uuid + el.type + renderKey}
            className={styles.watermarkElement}
            style={
              {
                transform: t.toString(),
                '--current-scale': safeScale,
                '--inverse-current-scale': 1 / safeScale,
                ...(unpositioned ? { visibility: 'hidden' } : {}),
              } as CSSProperties
            }
            ref={(ref) => {
              elementRefs.current.set(el.uuid, ref);
            }}
            onPointerDown={onMouseDown(mainRef, elementRefs, el, transforms, debugDot)}
          >
            <ControlBox />
            <SwitchCase input={el.type}>
              <Case value={WatermarkElementTypes.email}>
                <EmailEl {...el} />
              </Case>
              <Case value={WatermarkElementTypes.custom}>
                <TextEl {...el} />
              </Case>
              <Case value={[WatermarkElementTypes.date, WatermarkElementTypes.time, WatermarkElementTypes.datetime]}>
                <DateTimeEl {...el} />
              </Case>
              <Case value={WatermarkElementTypes.image}>
                <ImageEl {...el} />
              </Case>
            </SwitchCase>
          </div>
        );
      })}
    </div>
  );
}
